import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { AppLayout, StickyFooter } from '@/components/layout';
import { OrderReceiptCard, KioskGuaranteeBanner, SessionSecurityBar } from '@/components/payment/index';
import { ROUTES } from '@/constants/routes';
import { PAPER_SIZES } from '@/constants/printOptions';
import type { OrderReceiptSummary, PaymentPageState, ConfirmationPageState } from '@/types/payment.types';
import type { RazorpaySuccessResponse } from '@/types/razorpay.types';
import { paymentGatewayService } from '@/api/services/paymentGatewayService';
import { getApiErrorMessage } from '@/hooks/useApi';
import { useSessionStore } from '@/store/useSessionStore';
import toast from 'react-hot-toast';
import { kioskService } from '@/api/services/index';
import type { KioskInfo } from '@/types/kiosk.types';

type RzpOrder = { order_id: string; amount: number; currency: string; key_id: string };

export function PaymentPage() {
  const location = useLocation();
  const routeState = location.state as PaymentPageState | null;
  const storedState = useSessionStore((s) => s.paymentState);
  const setPaymentState = useSessionStore((s) => s.setPaymentState); 

  // Fresh from Options -> router state; after a refresh -> persisted copy
  const state = routeState ?? storedState; 

  useEffect(() => {
    if (routeState) setPaymentState(routeState);
  }, [routeState, setPaymentState]);

  if (!state) return <Navigate to={ROUTES.upload} replace />;
  return <PaymentPageContent state={state} />;
}

function PaymentPageContent({ state }: { state: PaymentPageState }) {
  const OTP_VALIDITY_MS = 24 * 60 * 60 * 1000;
  const navigate = useNavigate();
  const setPaymentState = useSessionStore((s) => s.setPaymentState); 

  const kiosk = useSessionStore((s) => s.kiosk);
  const setKiosk = useSessionStore((s) => s.setKiosk);

  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Money captured but our server verification failed
  const [pendingVerify, setPendingVerify] = useState<RazorpaySuccessResponse | null>(null);

  const mountedRef = useRef(true);
  const orderRef = useRef<RzpOrder | null>(null);
  const lastFailureRef = useRef<string | null>(null);
  const succeededRef = useRef(false);

  const setConfirmation = useSessionStore((s) => s.setConfirmation);

  const paperLabel = PAPER_SIZES.find((p) => p.id === state.paperSizeId)?.label ?? state.paperSizeId;

  // Merchant commission shown as its own line; the base charge line is the print price without it
  const serviceFee = state.merchantFeeTotal ?? 0;
  const serviceFeePerPage = state.totalImpressions > 0 ? serviceFee / state.totalImpressions : 0;

  const receipt: OrderReceiptSummary = {
    orderId: `KP-${state.printRequestId}`,
    fileName: state.fileName,
    colorMode: state.colorMode === 'bw' ? 'B&W' : 'Color',
    paperSize: paperLabel,
    duplexMode: state.isDuplex ? 'Duplex' : 'Single-sided',
    copies: state.copies,
    totalImpressions: state.totalImpressions,
    lineItems: [
      {
        label: 'Base Print Charges',
        sublabel: `${state.totalImpressions} pages × ₹${(state.rate - serviceFeePerPage).toFixed(2)}`,
        amount: state.basePrice - serviceFee,
      },
      ...(serviceFee > 0
        ? [{
            label: 'Service Fee',
            sublabel: `${state.totalImpressions} pages × ₹${serviceFeePerPage.toFixed(2)}`,
            amount: serviceFee,
          }]
        : []),
      ...(state.isDuplex && state.ecoDiscount > 0
        ? [{ label: 'Duplex Paper-Saver Rebate', amount: state.ecoDiscount, isDiscount: true }]
        : []),
      ...(state.stapleFee > 0 ? [{ label: 'Stapling', amount: state.stapleFee }] : []),
      ...((state.convenienceFee ?? 0) > 0
        ? [{ label: 'Convenience Fee', amount: state.convenienceFee ?? 0 }]
        : [{ label: 'Kiosk Terminal Fee', amount: 0, isFree: true }]),
    ],
    totalDue: state.total,
  };

  const goToSuccess = (otp: string, serverExpiresAt?: string | null) => {
    const confirmation: ConfirmationPageState = {
      otp,
      otpExpiresAt: serverExpiresAt ?? new Date(Date.now() + OTP_VALIDITY_MS).toISOString(),
      printRequestId: state.printRequestId,
      fileName: state.fileName,
      pageCount: state.pageCount,
      copies: state.copies,
      colorMode: state.colorMode,
      isDuplex: state.isDuplex,
      amountPaid: state.total,
    };
    setConfirmation(confirmation);
    setPaymentState(null);
    navigate(ROUTES.success, { replace: true, state: confirmation });
  };

  const verify = async (response: RazorpaySuccessResponse) => {
    setIsPaying(true);
    setPaymentError(null);
    try {
      const res = await paymentGatewayService.verifyPayment({
        ...response,
        print_request: state.printRequestId,
      });
      goToSuccess(res.data.data.otp, res.data.data.otp_expires_at);
    } catch (error) {
      if (!mountedRef.current) return;
      setIsPaying(false);
      setPendingVerify(response);
      setPaymentError(
        getApiErrorMessage(
          error,
          'Payment received but verification failed. Tap "Retry verification". Do not pay again.'
        )
      );
    }
  };

  const handlePay = async () => {
    if (isPaying || pendingVerify) return;
    setIsPaying(true);
    setPaymentError(null);
    lastFailureRef.current = null;

    try {
      if (!orderRef.current) {
        const res = await paymentGatewayService.createOrder(state.printRequestId);
        orderRef.current = res.data.data;
      }
      const { order_id, amount, currency, key_id } = orderRef.current;

      if (!mountedRef.current) return;

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: 'Print Buddie',
        description: state.fileName,
        handler: (response) => {
          try { localStorage.removeItem('kiosk-pay-started'); } catch { /* ignore */ }
          succeededRef.current = true;
          void verify(response);
        },
        modal: {
          ondismiss: () => {
            try { localStorage.removeItem('kiosk-pay-started'); } catch { /* ignore */ }
            if (!mountedRef.current || succeededRef.current) return;
            setIsPaying(false);
            setPaymentError(lastFailureRef.current ?? 'Payment was cancelled. You can try again.');
          },
        },
      });

      // Razorpay keeps its modal open so the user can retry inside it
      rzp.on('payment.failed', (r) => {
        lastFailureRef.current = r.error.description || 'Payment failed. Please try again.';
      });
      try { localStorage.setItem('kiosk-pay-started', String(Date.now())); } catch { /* ignore */ }
      rzp.open();
    } catch (error) {
      orderRef.current = null;
      if (mountedRef.current) {
        setIsPaying(false);
        setPaymentError(getApiErrorMessage(error, 'Could not start payment. Please try again.'));
        toast.error('Could not start payment');
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (kiosk || !state.kioskId) return; 

    (async () => {
      try {
        const response = await kioskService.getById(String(state.kioskId));
        const k = response.data.data;
        if (!k) return;

        const info: KioskInfo = {
          kioskId: String(k.id),
          label: k.name,
          location: k.location,
          status: k.is_active ? 'online' : 'offline',
          vendorName: k.vendor_name,
          printerModel: k.printer_model,
          printerSpecification: k.printer_specification,
          supportsColor: k.is_supports_color,
          supportsDuplex: k.is_supports_duplex,
          supportsA4: k.is_a4,
          supportsLetter: k.is_letter,
          supportsLegal: k.is_legal,
          a4Count: k.a4_count,
          letterCount: k.letter_count,
          legalCount: k.legal_count,
          vendorId: k.vendor,
          bwRatePerPage: k.rate_charge ? parseFloat(k.rate_charge.bw_rate_per_page) : null,
          colorRatePerPage: k.rate_charge ? parseFloat(k.rate_charge.color_rate_per_page) : null,
          duplexExtraCharge: k.rate_charge ? parseFloat(k.rate_charge.duplex_extra_charge) : null,
          merchantFees: k.rate_charge ? parseFloat(k.rate_charge.merchant_fees) || 0 : null,
          convenienceFee: k.rate_charge ? parseFloat(k.rate_charge.convenience_fee) || 0 : null,
        };
        setKiosk(info);
      } catch(e) {
        /* header stays blank; payment still works */
        toast.error(`kiosk fetch failed: ${String(e)}`, { duration: 10000 });
      }
    })();
  }, [kiosk, state.kioskId, setKiosk]);

  // useEffect(() => {
  //   try {
  //     const started = Number(localStorage.getItem('kiosk-pay-started'));
  //     if (started) {
  //       localStorage.removeItem('kiosk-pay-started');
  //       // only treat as interrupted if it was recent (15 min)
  //       if (Date.now() - started < 15 * 60 * 1000) {
  //         setPaymentError('Your payment was interrupted. You can try again.');
  //       }
  //     }
  //   } catch { /* ignore */ }
  // }, []); 
 
  return (
    <AppLayout title="Order Payment" currentStep={3}>  
      <SessionSecurityBar />

      {paymentError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2" role="alert">
          <span>
            <i className="bi bi-exclamation-triangle-fill me-2" />
            {paymentError}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger flex-shrink-0"
            disabled={isPaying}
            onClick={() => (pendingVerify ? void verify(pendingVerify) : void handlePay())}
          >
            {pendingVerify ? 'Retry verification' : 'Try again'}
          </button>
        </div>
      )}

      <Row className="g-3 g-lg-4">
        <Col xs={12} lg={5} className="order-1 order-lg-2">
          <OrderReceiptCard receipt={receipt} onPay={handlePay} isPaying={isPaying || !!pendingVerify} />
        </Col>
        <Col xs={12} lg={7} className="order-2 order-lg-1">
          <KioskGuaranteeBanner />
        </Col>
      </Row>

      <div className="d-lg-none">
        <StickyFooter>
          <button
            type="button"
            onClick={handlePay}
            disabled={isPaying || !!pendingVerify}
            className="btn btn-primary w-100 tap-target active-press d-flex align-items-center justify-content-center gap-2 text-label-lg"
            style={{ height: 52 }}
          >
            {isPaying ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <i className="bi bi-lock-fill" />
            )}
            <span>{isPaying ? 'Processing…' : paymentError ? 'Try again' : `Pay ₹${receipt.totalDue.toFixed(2)}`}</span>
          </button>
        </StickyFooter>
      </div>
    </AppLayout>
  );
}